import React, { useId } from 'react';
import styled from '@emotion/styled';
import { palette, spacing, typography } from '../../../../style-utils/theme';

/**
 * WP-43 — A DISABLED BUTTON WITH NO REASON IS A BROKEN BUTTON.
 *
 * The reasons existed; they were hidden in a native `title` tooltip. That is
 * the wrong place for all three of this product's readers:
 *
 *   - a desk tablet is the primary device, and `title` never fires on touch;
 *   - several browsers suppress `title` on a DISABLED control specifically,
 *     which is exactly when the reason matters;
 *   - screen-reader support for `title` is inconsistent, and on a disabled
 *     control the element is often skipped entirely.
 *
 * So the reason is rendered as visible text beside the control and wired to it
 * with `aria-describedby`. The pattern is lifted from iq-summary-card.tsx, which
 * already does this under the comment this one is named after.
 *
 * Usage — the render prop hands back the id to put on the control:
 *
 *   <ActionWithReason reason={disabled ? 'No prepaid units left.' : null}>
 *     {describedBy => (
 *       <PrimaryButton disabled={disabled} aria-describedby={describedBy}>
 *         + Add unit
 *       </PrimaryButton>
 *     )}
 *   </ActionWithReason>
 */

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.half};
`;

export const DisabledReason = styled.p`
  margin: 0;
  max-width: 32ch;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: 1.4;
  color: ${palette.slate};
  /* Wraps rather than pushing the row wide: this sits inside flex containers
     that would otherwise force a horizontal scrollbar at 320px. */
  overflow-wrap: anywhere;
`;

interface ActionWithReasonProps {
  /** The reason the control cannot be used, or null when it can. */
  reason?: string | null;
  children: (describedBy: string | undefined) => React.ReactNode;
}

export const ActionWithReason: React.FC<ActionWithReasonProps> = ({
  reason,
  children,
}) => {
  // Stable per instance, so several disabled controls on one screen do not
  // share an id and describe each other.
  const id = useId();
  const describedBy = reason ? `${id}-reason` : undefined;

  return (
    <Stack>
      {children(describedBy)}
      {reason ? (
        <DisabledReason id={describedBy}>{reason}</DisabledReason>
      ) : null}
    </Stack>
  );
};
